import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Trash2 } from "lucide-react";
import { api, errMsg } from "../lib/api";
import { rupiah, tgl, today } from "../lib/format";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";

const empty = { akun_debit: "", akun_kredit: "", keterangan: "", jumlah: "", tanggal: today() };

function AkunSelect({ value, onChange, coa, testid, placeholder }) {
  return (
    <Select value={value} onValueChange={onChange} required>
      <SelectTrigger data-testid={testid}><SelectValue placeholder={placeholder} /></SelectTrigger>
      <SelectContent>
        {coa.map((a) => <SelectItem key={a.nama} value={a.nama}>{a.nama} ({a.tipe})</SelectItem>)}
      </SelectContent>
    </Select>
  );
}

export default function Jurnal() {
  const [coa, setCoa] = useState([]);
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(empty);
  const [loading, setLoading] = useState(false);

  const load = useCallback(() => {
    api.get("/coa").then((r) => setCoa(r.data)).catch(() => {});
    api.get("/journals").then((r) => setItems(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (form.akun_debit === form.akun_kredit) {
      toast.error("Akun debit dan kredit tidak boleh sama");
      return;
    }
    setLoading(true);
    try {
      await api.post("/journals", { ...form, jumlah: Number(form.jumlah) });
      toast.success("Jurnal tercatat");
      setForm(empty);
      load();
    } catch (err) {
      toast.error(errMsg(err, "Gagal mencatat jurnal"));
    } finally {
      setLoading(false);
    }
  };

  const hapus = async (id) => {
    await api.delete(`/journals/${id}`);
    toast.success("Jurnal dihapus");
    load();
  };

  return (
    <div data-testid="jurnal-page">
      <p className="text-[10px] uppercase tracking-[0.15em] font-bold text-muted-foreground">Pembukuan Debit & Kredit</p>
      <h1 className="mt-1 text-2xl font-bold tracking-tight" style={{ fontFamily: "'Work Sans', sans-serif" }}>Jurnal Manual</h1>

      <form onSubmit={submit} className="mt-5 rounded-md border border-border bg-white p-5 space-y-4" data-testid="jurnal-form">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div className="space-y-1.5">
            <Label>Akun Debit</Label>
            <AkunSelect value={form.akun_debit} onChange={(v) => set("akun_debit", v)} coa={coa} testid="jurnal-debit-select" placeholder="Pilih akun debit..." />
          </div>
          <div className="space-y-1.5">
            <Label>Akun Kredit</Label>
            <AkunSelect value={form.akun_kredit} onChange={(v) => set("akun_kredit", v)} coa={coa} testid="jurnal-kredit-select" placeholder="Pilih akun kredit..." />
          </div>
        </div>
        <div className="rounded-md border border-border bg-muted/50 px-3 py-2 text-xs text-muted-foreground space-y-0.5" data-testid="jurnal-panduan">
          <p><b>Panduan arah jurnal:</b></p>
          <p>· Bayar biaya → Debit: akun biaya (cth Biaya Listrik), Kredit: Kas/Bank</p>
          <p>· Setor modal → Debit: Kas/Bank, Kredit: Modal</p>
          <p>· Jika angka beban di Laba Rugi tampil minus, berarti arah debit/kredit tertukar — hapus lalu catat ulang dengan arah yang benar.</p>
        </div>
        <div className="space-y-1.5">
          <Label>Keterangan</Label>
          <Input required value={form.keterangan} onChange={(e) => set("keterangan", e.target.value)} placeholder="cth: Setor modal / bayar listrik" data-testid="jurnal-keterangan-input" />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1.5">
            <Label>Jumlah (Rp)</Label>
            <Input required type="number" min="1" value={form.jumlah} onChange={(e) => set("jumlah", e.target.value)} data-testid="jurnal-jumlah-input" />
          </div>
          <div className="space-y-1.5">
            <Label>Tanggal</Label>
            <Input type="date" value={form.tanggal} onChange={(e) => set("tanggal", e.target.value)} data-testid="jurnal-tanggal-input" />
          </div>
        </div>
        <Button type="submit" className="w-full rounded-full active:scale-95 transition-transform duration-100" disabled={loading || !form.akun_debit || !form.akun_kredit} data-testid="jurnal-submit-button">
          {loading ? "Menyimpan..." : "Catat Jurnal"}
        </Button>
      </form>

      <div className="mt-6 rounded-md border border-border bg-white divide-y divide-border" data-testid="jurnal-list">
        {items.length === 0 && <p className="p-4 text-sm text-muted-foreground">Belum ada jurnal tercatat.</p>}
        {items.map((j) => {
          const debit = j.akun_debit || j.akun_nama;
          const kredit = j.akun_kredit || (j.metode === "transfer" ? "Bank" : "Kas");
          return (
            <div key={j.id} className="flex items-center justify-between p-3.5" data-testid={`jurnal-item-${j.id}`}>
              <div>
                <p className="text-sm font-semibold">{j.keterangan}</p>
                <p className="text-xs text-muted-foreground">Debit: {debit} · Kredit: {kredit}</p>
                <p className="text-xs text-muted-foreground">{tgl(j.tanggal)}{j.dibuat_oleh ? ` · ${j.dibuat_oleh}` : ""}</p>
              </div>
              <div className="flex items-center gap-2">
                <p className="text-sm font-bold">{rupiah(j.jumlah)}</p>
                <button onClick={() => hapus(j.id)} data-testid={`jurnal-delete-${j.id}`} className="rounded-md p-1.5 text-muted-foreground hover:text-destructive transition-colors duration-150 active:scale-95">
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
